import { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react"
import { Alert, Box, Button, CircularProgress, Tooltip, Typography } from "@mui/material"
import type { SxProps, Theme } from "@mui/material"
import { EventRepeatOutlined, WarningAmberOutlined } from "@mui/icons-material"
import { DataGridPremium, useGridApiRef } from "@mui/x-data-grid-premium"
import type { GridColDef, GridFilterModel, GridRenderCellParams, GridSortModel } from "@mui/x-data-grid-premium"
import { visuallyHidden } from "@mui/utils"
import { NotFoundError } from "api/errors"
import { useAppDispatch, useAppSelector } from "hooks/redux"
import { useRefetchScheduledWorkflowsAt, useScheduledWorkflow, useScheduledWorkflows } from "resources/scheduled-workflows/hooks"
import type { ScheduledWorkflowListItem } from "resources/scheduled-workflows/types"
import { ToolbarWithSearch } from "../chatbox/toolbar-with-search"
import { selectDockView, selectWorkflowToOpen, setWorkflowToOpen } from "../store/agent-chat-slice"
import { KELLY_LINK_TYPES, KellyLinkContext, useKellyLinkNavigate } from "../kelly-link-types"
import { FilterPill, SURFACE_HEADER_SX, SURFACE_PILLS_SX, surfaceGridBaseSx, surfaceRootSx } from "../chatbox/surface-styles"
import { formatReportTime, formatSchedule, nextBudgetReset, scheduleStatusLine, viewerTimeZone } from "./schedule-format"
import { RUN_OUTCOME_DISPLAY, failedTooltip, latestReportLabel, latestReportOf } from "./latest-report"
import { WorkflowReportView } from "./report-panel"
import { ReconnectButton, RecoverButton, RowActions } from "./row-actions"
import { useNow } from "./use-now"

const SURFACE_CONTAINER = "kelly-workflows-surface"
const ROOT_SX = surfaceRootSx(SURFACE_CONTAINER)
const GRID_SX = ((theme: Theme) => ({
  ...surfaceGridBaseSx(theme, SURFACE_CONTAINER),
  "& .MuiDataGrid-columnSeparator": { display: "none" },
  "& .MuiDataGrid-row:hover": { backgroundColor: "action.hover" },
  "& .MuiDataGrid-cell:focus": { outline: "none" },
  "& .MuiDataGrid-cell": { display: "flex", flexDirection: "column", justifyContent: "center" },
  "&.MuiDataGrid-root--densityCompact .MuiDataGrid-cell": { py: 1 },
})) satisfies SxProps<Theme>
const CENTER_SX = { display: "flex", justifyContent: "center", p: 3 } satisfies SxProps<Theme>
const EMPTY_SX = { display: "flex", flexDirection: "column", alignItems: "center", textAlign: "center", gap: 0.5, px: 3, py: 6 } satisfies SxProps<Theme>
const EMPTY_GLYPH_SX = { fontSize: "2.5rem", mb: 1, color: "text.disabled" } satisfies SxProps<Theme>
const BUDGET_RESET_SETTLE_MILLISECONDS = 5_000
const BUDGET_RESET_RETRY_WINDOW_MILLISECONDS = 60_000
const BUDGET_RESET_RETRY_MILLISECONDS = 15_000
const ELLIPSIS_SX = { maxWidth: "100%", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" } satisfies SxProps<Theme>
const NAME_SX = { ...ELLIPSIS_SX, fontWeight: "bold", display: "block" } satisfies SxProps<Theme>
const SECONDARY_LINE_SX = { ...ELLIPSIS_SX, color: "text.secondary" } satisfies SxProps<Theme>
const BLOCKED_LINE_SX = { display: "flex", alignItems: "center", gap: 0.5 } satisfies SxProps<Theme>
const BLOCKED_ICON_SX = { fontSize: "1rem", color: "warning.main", flexShrink: 0 } satisfies SxProps<Theme>
const REPORT_SX = { display: "flex", alignItems: "center", gap: 0.75 } satisfies SxProps<Theme>
const REPORT_BUTTON_SX = { p: 0, minWidth: 0, textTransform: "none" } satisfies SxProps<Theme>
const NAME_BUTTON_SX = { p: 0, minWidth: 0, textTransform: "none", justifyContent: "flex-start", maxWidth: "100%", color: "text.primary" } satisfies SxProps<Theme>

const FILTERS = [
  { id: "active", label: "Active" },
  { id: "archived", label: "Archived" },
] as const

type WorkflowFilter = typeof FILTERS[number]["id"]

const EMPTY_COPY: Record<WorkflowFilter, string> = {
  active: "Ask Kelly to set up a workflow.",
  archived: "No archived workflows.",
}

function WorkflowsToolbar() {
  return <ToolbarWithSearch placeholder="Search workflows…" ariaLabel="Search workflows" />
}

interface LatestReportCellProps {
  workflow: ScheduledWorkflowListItem
  now: Date
  onOpenReport?: (deliverableId: string) => void
}

function LatestReportCell({ workflow, now, onOpenReport }: LatestReportCellProps) {
  const report = latestReportOf(workflow.lastRun, workflow.latestDeliverable)
  const { color, icon: Icon } = RUN_OUTCOME_DISPLAY[report.state]
  const label = latestReportLabel(report, (time) => formatReportTime(time, now))
  const deliverable = workflow.latestDeliverable
  const content = (
    <Box sx={REPORT_SX} tabIndex={report.state === "failed" ? 0 : undefined}>
      {report.state === "running" ? <CircularProgress size={14} color="info" /> : <Icon fontSize="small" sx={{ color }} />}
      {report.state === "report" && onOpenReport != null && deliverable != null
        ? <Button size="small" variant="text" sx={REPORT_BUTTON_SX} onClick={(event) => { event.currentTarget.focus(); onOpenReport(deliverable.deliverableId) }}>{label}</Button>
        : <Typography variant="body2">{label}</Typography>}
    </Box>
  )
  if (report.state !== "failed") return content
  return <Tooltip title={failedTooltip(workflow.lastRun?.errorType ?? null)}>{content}</Tooltip>
}

export function ScheduledWorkflowsSurface({ onOpenReport }: { onOpenReport: (deliverableId: string) => void }) {
  const [filter, setFilter] = useState<WorkflowFilter>("active")
  const [sortModel, setSortModel] = useState<GridSortModel>([{ field: "latestReport", sort: "desc" }])
  const [filterModel, setFilterModel] = useState<GridFilterModel>({ items: [] })
  const listRef = useRef<HTMLDivElement>(null)
  const apiRef = useGridApiRef()
  const lastOpenedIdRef = useRef<string | null>(null)
  const { workflows, dataUpdatedAt, isLoading, isError, isIncomplete } = useScheduledWorkflows({ includeArchived: filter === "archived" })
  const now = useNow()
  const dispatch = useAppDispatch()
  const navigate = useKellyLinkNavigate()
  const { link } = useContext(KellyLinkContext)
  const dockView = useAppSelector(selectDockView)
  const linkedWorkflowLoad = KELLY_LINK_TYPES["scheduled-workflows"].useLoad(dockView === "full" && link?.type === "scheduled-workflows" ? link.id : null)
  const [selectedWorkflowId, setSelectedWorkflowId] = useState<string | null>(null)
  const workflowToOpen = useAppSelector(selectWorkflowToOpen)
  if (workflowToOpen != null && workflowToOpen !== selectedWorkflowId) setSelectedWorkflowId(workflowToOpen)

  useEffect(() => {
    if (workflowToOpen != null) dispatch(setWorkflowToOpen(null))
  }, [workflowToOpen, dispatch])

  const { data: fetchedWorkflow, isLoading: isFetchingWorkflow, error: workflowError, refetch: refetchWorkflow } = useScheduledWorkflow(dockView === "full" ? null : selectedWorkflowId)
  // Until Expand writes the docked workflow into the address, the docked one stays open, so the list never paints.
  const openedWorkflowId = dockView === "full" ? (link?.type === "scheduled-workflows" ? link.id ?? selectedWorkflowId : null) : selectedWorkflowId
  useEffect(() => {
    if (openedWorkflowId != null) { lastOpenedIdRef.current = openedWorkflowId; return }
    const id = lastOpenedIdRef.current
    lastOpenedIdRef.current = null
    if (id == null) return
    let frame = 0
    let attempts = 0
    const focusRow = () => {
      const button = listRef.current?.querySelector<HTMLElement>(`[data-id="${CSS.escape(id)}"] [data-field="name"] button`)
      if (button != null) {
        if (document.activeElement === document.body || document.activeElement === null) button.focus()
      } else {
        const rowIndex = apiRef.current?.getRowIndexRelativeToVisibleRows(id)
        if (rowIndex !== undefined && rowIndex >= 0) apiRef.current?.scrollToIndexes({ rowIndex })
      }
      attempts += 1
      if (attempts < 60) frame = requestAnimationFrame(focusRow)
    }
    const stop = () => cancelAnimationFrame(frame)
    const inputEvents = ["pointerdown", "keydown", "wheel"]
    inputEvents.forEach((name) => document.addEventListener(name, stop, true))
    focusRow()
    return () => {
      cancelAnimationFrame(frame)
      inputEvents.forEach((name) => document.removeEventListener(name, stop, true))
    }
  }, [openedWorkflowId, apiRef])
  if (dockView === "full" && selectedWorkflowId != null && link?.type === "scheduled-workflows" && link.id === selectedWorkflowId) setSelectedWorkflowId(null)
  // Minimise keeps the workflow the address showed in the docked Kelly.
  if (dockView !== "full" && link?.type === "scheduled-workflows" && link.id != null && link.id !== selectedWorkflowId) setSelectedWorkflowId(link.id)
  const wasDockedRef = useRef(dockView !== "full")
  useEffect(() => {
    const wasDocked = wasDockedRef.current
    wasDockedRef.current = dockView !== "full"
    if (!wasDocked || dockView !== "full" || link?.id != null || selectedWorkflowId == null) return
    navigate("scheduled-workflows", selectedWorkflowId, { replace: true })
  }, [dockView, link, selectedWorkflowId, navigate])

  const selectWorkflow = useCallback((scheduledWorkflowId: string | null) => {
    if (!navigate("scheduled-workflows", scheduledWorkflowId)) setSelectedWorkflowId(scheduledWorkflowId)
  }, [navigate])
  const handleBack = useCallback(() => selectWorkflow(null), [selectWorkflow])

  const listedAt = useMemo(() => new Date(dataUpdatedAt), [dataUpdatedAt])
  const hasOverBudgetRow = workflows?.some((workflow) => workflow.blockedReason === "over_budget") ?? false
  const isListedJustAfterReset = listedAt.getTime() - Date.UTC(listedAt.getUTCFullYear(), listedAt.getUTCMonth(), listedAt.getUTCDate()) < BUDGET_RESET_RETRY_WINDOW_MILLISECONDS
  const budgetRefetchAt = isListedJustAfterReset ? listedAt.getTime() + BUDGET_RESET_RETRY_MILLISECONDS : nextBudgetReset(listedAt).getTime() + BUDGET_RESET_SETTLE_MILLISECONDS
  useRefetchScheduledWorkflowsAt(hasOverBudgetRow ? budgetRefetchAt : null)

  const rows = useMemo(
    () => (workflows ?? []).filter((workflow) => (filter === "archived") === (workflow.archivedAt != null)),
    [workflows, filter],
  )

  const columns = useMemo<GridColDef<ScheduledWorkflowListItem>[]>(() => [
    {
      field: "name",
      headerName: "Workflow",
      flex: 1,
      minWidth: 180,
      valueGetter: (_value, row) => `${row.name} ${row.description ?? ""}`,
      renderCell: ({ row }: GridRenderCellParams<ScheduledWorkflowListItem>) => (
        <>
          <Button size="small" variant="text" sx={NAME_BUTTON_SX} onClick={() => selectWorkflow(row.scheduledWorkflowId)}>
            <Typography variant="body2" component="span" sx={NAME_SX} title={row.name}>{row.name}</Typography>
          </Button>
          {row.description != null && <Typography variant="caption" sx={SECONDARY_LINE_SX} title={row.description}>{row.description}</Typography>}
        </>
      ),
    },
    {
      field: "schedule",
      headerName: "Schedule",
      flex: 1,
      minWidth: 160,
      sortable: false,
      getApplyQuickFilterFn: () => null,
      renderCell: ({ row }: GridRenderCellParams<ScheduledWorkflowListItem>) => {
        const statusLine = scheduleStatusLine(row, now, viewerTimeZone(), listedAt)
        const statusText = <Typography variant="caption" sx={SECONDARY_LINE_SX} title={statusLine ?? undefined}>{statusLine}</Typography>
        return (
          <>
            {row.schedule != null && <Typography variant="body2" sx={ELLIPSIS_SX}>{formatSchedule(row.schedule, viewerTimeZone(), { now, nextRunAt: row.nextRunAt })}</Typography>}
            {row.blockedReason != null ? <Box sx={BLOCKED_LINE_SX}><WarningAmberOutlined sx={BLOCKED_ICON_SX} />{statusText}</Box> : statusText}
            <ReconnectButton workflow={row} />
            <RecoverButton workflow={row} />
          </>
        )
      },
    },
    {
      field: "latestReport",
      headerName: "Latest report",
      flex: 0.8,
      minWidth: 150,
      getApplyQuickFilterFn: () => null,
      valueGetter: (_value, row) => {
        const { time } = latestReportOf(row.lastRun, row.latestDeliverable)
        return time == null ? Number.NEGATIVE_INFINITY : new Date(time).getTime()
      },
      renderCell: ({ row }: GridRenderCellParams<ScheduledWorkflowListItem>) => <LatestReportCell workflow={row} now={now} onOpenReport={onOpenReport} />,
    },
    {
      field: "actions",
      headerName: "Actions",
      renderHeader: () => <Box component="span" sx={visuallyHidden}>Actions</Box>,
      width: 136,
      sortable: false,
      align: "right",
      renderCell: ({ row }: GridRenderCellParams<ScheduledWorkflowListItem>) => <RowActions workflow={row} />,
    },
  ], [now, listedAt, onOpenReport, selectWorkflow])

  const linkedWorkflow = linkedWorkflowLoad.status === "found" ? linkedWorkflowLoad.value : undefined
  const selectedWorkflow = openedWorkflowId == null ? undefined : workflows?.find((workflow) => workflow.scheduledWorkflowId === openedWorkflowId) ?? (dockView === "full" ? linkedWorkflow : fetchedWorkflow)
  if (selectedWorkflow != null) {
    return (
      <Box sx={ROOT_SX} data-testid="scheduled-workflows-surface">
        <WorkflowReportView key={selectedWorkflow.scheduledWorkflowId} workflow={selectedWorkflow} onBack={handleBack} onOpenReport={onOpenReport} />
      </Box>
    )
  }
  if (openedWorkflowId != null && (dockView === "full" ? linkedWorkflowLoad.status === "pending" : isFetchingWorkflow)) {
    return (
      <Box sx={ROOT_SX} data-testid="scheduled-workflows-surface">
        <CircularProgress size={20} aria-label="Loading workflow" />
      </Box>
    )
  }
  const isOpenErrored = dockView === "full" ? linkedWorkflowLoad.status === "error" : workflowError != null
  if (openedWorkflowId != null && isOpenErrored) {
    return (
      <Box sx={ROOT_SX} data-testid="scheduled-workflows-surface">
        <Alert
          severity="error"
          data-testid="scheduled-workflow-open-error"
          action={(
            <>
              {!(workflowError instanceof NotFoundError) && <Button color="inherit" size="small" onClick={() => (linkedWorkflowLoad.status === "error" ? linkedWorkflowLoad.retry() : void refetchWorkflow())}>Try again</Button>}
              <Button color="inherit" size="small" onClick={handleBack}>Back</Button>
            </>
          )}
        >
          {workflowError instanceof NotFoundError ? "This workflow is not available." : "Couldn't open this workflow. Please try again."}
        </Alert>
      </Box>
    )
  }

  return (
    <Box ref={listRef} sx={ROOT_SX} data-testid="scheduled-workflows-surface">
      <Box sx={SURFACE_HEADER_SX}>
        <Typography variant="h6" component="h2">Scheduled Workflows</Typography>
      </Box>
      {isError && workflows == null && <Alert severity="error" data-testid="scheduled-workflows-error">Couldn&apos;t load your workflows. Please try again later.</Alert>}
      {isIncomplete && workflows != null && <Alert severity="warning" data-testid="scheduled-workflows-incomplete">Couldn&apos;t load all your workflows. Please try again later.</Alert>}
      <Box sx={SURFACE_PILLS_SX} role="group" aria-label="Filter workflows">
        {FILTERS.map(({ id, label }) => (
          <FilterPill
            key={id}
            label={label}
            size="small"
            variant="outlined"
            isSelected={filter === id}
            onClick={() => { setFilter(id); selectWorkflow(null) }}
            aria-pressed={filter === id}
            data-testid={`scheduled-workflows-filter-${id}`}
          />
        ))}
      </Box>

      {isLoading && rows.length === 0 && (
        <Box sx={CENTER_SX} data-testid="scheduled-workflows-loading">
          <CircularProgress size={20} aria-label="Loading workflows" />
        </Box>
      )}
      {!isLoading && workflows != null && rows.length === 0 && (
        <Box sx={EMPTY_SX} data-testid="scheduled-workflows-empty">
          <EventRepeatOutlined sx={EMPTY_GLYPH_SX} />
          <Typography variant="body2" color="textSecondary">{EMPTY_COPY[filter]}</Typography>
        </Box>
      )}
      {rows.length > 0 && (
        <DataGridPremium
          apiRef={apiRef}
          rows={rows}
          columns={columns}
          getRowId={(row) => row.scheduledWorkflowId}
          getRowHeight={() => "auto"}
          density="compact"
          disableRowSelectionOnClick
          onRowClick={({ row }, event) => {
            if (!(event.target instanceof Element) || !event.currentTarget.contains(event.target) || event.target.closest("button, a, input, [role='menuitem'], [role='switch']") != null) return
            const { clientX, clientY } = event
            const isOnDisabledControl = Array.from(event.currentTarget.querySelectorAll(".Mui-disabled")).some((control) => {
              const { left, right, top, bottom } = (control.closest(".MuiSwitch-root") ?? control).getBoundingClientRect()
              return clientX > left && clientX < right && clientY > top && clientY < bottom
            })
            if (isOnDisabledControl) return
            selectWorkflow(row.scheduledWorkflowId)
          }}
          disableColumnMenu
          disableColumnResize
          hideFooter
          sx={GRID_SX}
          slots={{ toolbar: WorkflowsToolbar }}
          showToolbar
          sortModel={sortModel}
          onSortModelChange={setSortModel}
          filterModel={filterModel}
          onFilterModelChange={setFilterModel}
          data-testid="scheduled-workflows-grid"
        />
      )}
    </Box>
  )
}
